import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Grid, Typography, Box, Chip,
  Paper, CardMedia, Rating, Badge, Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  Button,
  IconButton,
  useTheme,
  Tooltip,
  Dialog,
  DialogContent,
  Fade,
  Divider
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StoreIcon from '@mui/icons-material/Store';
import ShareIcon from '@mui/icons-material/Share';
import { api } from '../services/api';
import ProductCard from '../components/ProductCard';
import LoadingScreen from '../components/LoadingScreen';
import VerifiedIcon from '@mui/icons-material/Verified';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, DeleteSweep as DeleteSweepIcon } from '@mui/icons-material';
import { CATEGORY_HIERARCHY } from '../constants/categories';
import LdPlaceholder from '../assets/ldplaceholder.png';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderIcon from '@mui/icons-material/Folder';
import DownloadIcon from '@mui/icons-material/Download';  
import CopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import EditButtonDialog from '../components/EditButtonDialog';
import { auth } from '../utils/auth';
import { UNIT_LABELS_SINGULAR, UNIT_LABELS_PLURAL } from '../utils/units';
import EditStyleDialog from '../components/EditStyleDialog';
import PaletteIcon from '@mui/icons-material/Palette';
import {
  Phone as PhoneIcon,
  Link as LinkIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Language as WebIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const AVAILABLE_ICONS = [
  'WhatsAppIcon',
  'EmailIcon',
  'PictureAsPdfIcon',
  'PhoneIcon',
  'LocationOnIcon',
  'LinkIcon',
  'InstagramIcon',
  'FacebookIcon',
  'TwitterIcon',
  'LinkedInIcon',
  'WebIcon',
  'ShoppingCartIcon',
  'StoreIcon',
  'LocalShippingIcon',
  'PaymentIcon',
  'InfoIcon'
];

const iconComponents = {
  WhatsAppIcon,
  EmailIcon,
  PictureAsPdfIcon,
  PhoneIcon,
  LocationOnIcon,
  LinkIcon,
  InstagramIcon,
  FacebookIcon,
  TwitterIcon,
  LinkedInIcon,
  WebIcon,
  ShoppingCartIcon,
  StoreIcon,
  LocalShippingIcon,
  PaymentIcon,
  InfoIcon
};

const DistributorPage = () => {
  const { id } = useParams();
  const [distributor, setDistributor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [sortBy, setSortBy] = useState('createdAt-desc');
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterMenuOpen, setFilterMenuOpen] = useState(!isMobile);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [buttons, setButtons] = useState([]);
  
  const [customStyle, setCustomStyle] = useState({
    catalogBackgroundColor: 'rgb(233, 233, 233)',
    headerBackgroundColor: 'rgb(236, 236, 236)',
    primaryTextColor: '#000000',
    secondaryTextColor: 'white',
    nameColor: '#000000',
    descriptionColor: '#000000'
  });
  const [styleDialogOpen, setStyleDialogOpen] = useState(false);
  //TODO CHANGET HIS BACK FOR SECURITY
  const isOwner = distributor?._id === auth.getUser()?._id;
  const hasEditPermission = isOwner || (localStorage.getItem('subUserPermissions')?.includes('canEditProfile'));


  const handleCopyNumber = () => {
    navigator.clipboard.writeText(distributor.phone);
  }

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(distributor.email);
  }

  const handleDownload = () => {
    window.open(`https://drive.google.com/file/d/1/view`, '_blank');
  }

  // Auxiliary functions from LandingPage
  const getSubcategories = (category) => {
    if (!category) return [];
    if (CATEGORY_HIERARCHY[category]) {
      return Object.keys(CATEGORY_HIERARCHY[category].subcategories);
    }
    return [];
  };

  const getLeafCategories = (mainCategory, subCategory) => {
    if (!mainCategory || !subCategory) return [];
    return CATEGORY_HIERARCHY[mainCategory]?.subcategories[subCategory] || [];
  };

  const getAllCategories = (mainCategory) => {
    if (!mainCategory) return [];
    const categoryData = CATEGORY_HIERARCHY[mainCategory];
    if (!categoryData || !categoryData.subcategories) return [];

    return getSubcategories(mainCategory).flatMap(sub => {
      // Get leaf categories for this subcategory
      const leafCategories = getLeafCategories(mainCategory, sub);
      // Return both the subcategory and its leaf categories
      return [sub, ...leafCategories];
    });
  };

  const handleShare = () => {
    if (isMobile && navigator.share) {
      navigator.share({
        title: distributor.name,
        text: distributor.description,
        url: `${window.location.origin}/distributor/${distributor._id}` 
      });
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/distributor/${distributor._id}`);
      setShowCopyDialog(true);
      setTimeout(() => setShowCopyDialog(false), 1500); // Auto-hide after 1.5 seconds
    }
  };
  
  // Add the helper function
  const categoryHasProducts = (category, products, mainCategory = null) => {
    return products.some(product => {
      if (mainCategory) {
        // For subcategories and leaf categories
        const allCategories = getAllCategories(mainCategory);
        return allCategories.includes(product.category) && 
               (product.category === category || getLeafCategories(mainCategory, category).includes(product.category));
      }
      // For main categories
      const allCategories = getAllCategories(category);
      return allCategories.includes(product.category);
    });
  };

  const handleEditButton = (button) => {
    setEditingButton(button);
    setEditDialogOpen(true);
  };

  const handleSaveButton = async (updatedButton) => {
    try {
      const newButtons = [...buttons];
      const index = newButtons.findIndex(b => b.order === updatedButton.order);
      
      if (index >= 0) {
        newButtons[index] = updatedButton;
      } else {
        newButtons.push(updatedButton);
      }

      await api.updateDistributorProfile(distributor._id, {
        customButtons: newButtons
      });

      setButtons(newButtons);
      setEditDialogOpen(false);
      setEditingButton(null);
    } catch (error) {
      console.error('Error saving button:', error);
    }
  };

  const handleSaveStyle = async (newStyle) => {
    try {
      await api.updateDistributorProfile(distributor._id, {
        customStyle: newStyle
      });
      setCustomStyle(newStyle);
      setStyleDialogOpen(false);
    } catch (error) {
      console.error('Error saving style:', error);
    }
  };

  const handleDeleteButton = async (buttonToDelete) => {
    try {
      const newButtons = buttons.filter(b => b.order !== buttonToDelete.order);
      // Reorder remaining buttons
      newButtons.forEach((button, index) => {
        button.order = index;
      });

      await api.updateDistributorProfile(distributor._id, {
        customButtons: newButtons
      });

      setButtons(newButtons);
    } catch (error) {
      console.error('Error deleting button:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [distributorData, productsData] = await Promise.all([
          api.getDistributor(id),
          api.getDistributorProducts(id)
        ]);
        setDistributor(distributorData);
        setProducts(productsData);
      } catch (error) {
        setError('Error al cargar la información del distribuidor');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (!distributor) return;
    
    if (distributor.customButtons?.length > 0) {
      setButtons(distributor.customButtons);
    } else {
      const defaultButtons = [
        {
          text: 'Contactar por WhatsApp',
          icon: 'WhatsAppIcon',
          href: `https://wa.me/${distributor.phone?.replace(/\D/g,'')}`,
          hasCopy: true,
          copyText: distributor.phone,
          backgroundColor: 'rgb(35, 170, 84)',
          textColor: customStyle.secondaryTextColor,
          order: 0
        },
        {
          text: distributor.email,
          icon: 'EmailIcon',
          href: `mailto:${distributor.email}`,
          hasCopy: true,
          copyText: distributor.email,
          backgroundColor: 'rgb(230, 167, 52)',
          textColor: customStyle.secondaryTextColor,
          order: 1
        },
        {
          text: 'Lista en PDF',
          icon: 'PictureAsPdfIcon',
          href: `https://drive.google.com/file/d/${distributor.externalUrls?.[0]}/view`,
          hasCopy: false,
          backgroundColor: 'rgb(8, 161, 149)',
          textColor: customStyle.secondaryTextColor,
          order: 2
        }
      ];
      setButtons(defaultButtons);
    }
  }, [distributor]);

  useEffect(() => {
    if (!distributor) return;
    
    if (distributor.customStyle) {
      setCustomStyle(distributor.customStyle);
    }
  }, [distributor]);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...products];
    
    // Apply category filters
    if (selectedSubCategory) {
      const leafCategories = getLeafCategories(selectedMainCategory, selectedSubCategory);
      if (leafCategories.length > 0) {
        result = result.filter(product => leafCategories.includes(product.category));
      } else {
        result = result.filter(product => product.category === selectedSubCategory);
      }
    } else if (selectedMainCategory) {
      const allCategories = getAllCategories(selectedMainCategory);
      result = result.filter(product => allCategories.includes(product.category));
    }

    // Apply sorting
    const [sortField, sortOrder] = sortBy.split('-');
    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'createdAt') {
        comparison = new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortField === 'views') {
        comparison = (b.views || 0) - (a.views || 0);
      }
      return sortOrder === 'desc' ? comparison : -comparison;
    });

    setFilteredProducts(result);
  }, [products, selectedMainCategory, selectedSubCategory, sortBy]);

  

  if (loading) return <LoadingScreen message="Cargando distribuidor..." />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!distributor) return <Typography>No se encontró el distribuidor</Typography>;
  if (isMobile) {
    return (
      <Container maxWidth="lg" sx={{ 
        pt: 2, 
        bgcolor: customStyle.headerBackgroundColor 
      }}>
        {/* Background Image */}
        {/* <Box sx={{
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '150vw',
          zIndex: -1
        }}>
          <img 
            src={distributor.bannerImage || distributor.profileImage} 
            alt={distributor.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: 1
            }}
          />
        </Box> */}
        {/* Header Section - Profile + Name */}
            <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          mb: 2
        }}>
          {/* Profile Image */}
              {(distributor.profileImage || distributor.logoUrl) ? (
                <Avatar
                  src={distributor.profileImage || distributor.logoUrl}
                  alt={distributor.name}
                  sx={{
                width: 80,
                height: 80,
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.1)'
                  }}
                />
              ) : (
                <Avatar sx={{ 
              width: 80, 
              height: 80,
                  bgcolor: 'grey.200' 
                }}>
              <StoreIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                </Avatar>
              )}
          
          {/* Name and Verification */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: customStyle.nameColor }}>
                {distributor.name}
              </Typography>
              {distributor.status === 'verified' && (
                <Tooltip title="Este vendedor ha sido verificado por Liderdist.">
                  <VerifiedIcon color="primary" sx={{ fontSize: 20 }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" color={customStyle.descriptionColor}>
              {distributor.governmentName}
            </Typography>
          </Box>
          
          {/* Share Button */}
          <IconButton onClick={handleShare}>
            <ShareIcon />
          </IconButton>
            </Box>


              
      
          <Divider sx={{mb: 2}}/>
  {/* Description */}
  <Typography variant="body2" sx={{ color: customStyle.descriptionColor }}>
            {distributor.description}
          </Typography>
        {/* Distribution Zones */}
        <Box sx={{ p: 1, mb: 2, borderRadius: '1px'}}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' ,color: customStyle.primaryTextColor}}>
            Envíos a:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {distributor.zones.map((zone, index) => (
                  <Chip
                    key={index}
                icon={<LocationOnIcon sx={{ fontSize: '0.9rem', color: 'rgb(255, 136, 0)' }} />}
                    label={zone}
                    size="small"
                sx={{ fontSize: '0.8rem', color: customStyle.primaryTextColor }}
                  />
                ))}
              </Box>
            </Box>

        {/* Contact Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3,mt: 4, position: 'relative' }}>
          {hasEditPermission && (
            <Box sx={{ position: 'absolute', top: -35, right: 0, display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => setStyleDialogOpen(true)}
                sx={{
                  fontSize: '0.8rem'
                }}
              >
                Editar paleta de página 
                <PaletteIcon sx={{fontSize: 17,ml: 1}}/>
              </IconButton>
              <IconButton
                onClick={() => setIsEditing(prev => !prev)}
                sx={{
                  fontSize: '0.8rem'
                }}
              >
                Editar botones
                <EditIcon sx={{fontSize: 17,ml: 1}}/>
              </IconButton>
            </Box>
          )}

          {buttons.map((button) => (
              <Box key={button.order} sx={{ position: 'relative' }}>
                <Button
                  fullWidth
                  variant="contained"
                  href={!isEditing ? button.href : undefined}
                  startIcon={iconComponents[button.icon] && 
                    React.createElement(iconComponents[button.icon], {sx: {fontSize: 24}})}
                  sx={{ 
                    py: 1.5,
                    bgcolor: button.backgroundColor,
                    color: button.textColor || '#ffffff',
                    '&:hover': {
                      bgcolor: `${button.backgroundColor}dd`
                    }
                  }}
                >
                  {button.text}
                  <Box 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isEditing) {
                        handleEditButton(button);
                      } else if (button.hasCopy) {
                        navigator.clipboard.writeText(button.copyText);
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      width: '12%',
                      bgcolor: 'rgba(0, 0, 0, 0.1)',
                      borderLeft: '1px solid rgba(255, 255, 255, 0.29)',
                    }}
                  >
                    {isEditing ? (
                      <EditIcon sx={{fontSize: 17}}/>
                    ) : <CopyIcon sx={{fontSize: 17}}/>}
                  </Box>
                </Button>
                {/* Add delete button that appears when editing */}
                {isEditing && button.order > 2 && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteButton(button)}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'error.dark',
                      },
                      width: 24,
                      height: 24,
                      zIndex: 1
                    }}
                  >
                    <DeleteSweepIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            ))}

          {/* Add new button option when in edit mode */}
          {isEditing && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                const newButton = {
                  text: 'Nuevo Botón',
                  icon: 'WhatsAppIcon',
                  href: '',
                  hasCopy: false,
                  backgroundColor: 'rgb(128, 128, 128)',
                  textColor: '#ffffff',
                  order: buttons.length // This ensures the new button appears at the end
                };
                setEditingButton(newButton);
                setEditDialogOpen(true);
              }}
              sx={{ 
                py: 1.5,
                borderStyle: 'dashed',
                borderWidth: 2,
                '&:hover': {
                  borderStyle: 'dashed',
                  borderWidth: 2
                }
              }}
            >
              Agregar nuevo botón
            </Button>
          )}
        </Box>

        <Box sx={{width: '105vw', marginLeft: '-6vw', px: '5vw', pb: 2, bgcolor: isMobile ? customStyle.catalogBackgroundColor : 'transparent'}}>
           {/* Compact Filter Header - Only this part is changed */}
           <Paper sx={{ p: 1,px:3, mb: 1, width: '105vw', marginLeft: '-6vw', borderRadius: '2px'}}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    Filtros y Categorías
                  </Typography>
                  {(selectedMainCategory || selectedSubCategory) && (
                    <Chip 
                      label={selectedSubCategory || selectedMainCategory} 
                      size="small"
                      onDelete={() => {
                        setSelectedMainCategory('');
                        setSelectedSubCategory('');
                      }}
                    />
                  )}
                </Box>
                <IconButton size="small">
                  {filterMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Paper>
        {/* Products Section with Filters */}
        <Grid container spacing={3}>
          {/* Filters Section */}
          <Grid item xs={12} md={3}>

            {/* Keep the original filter content, just collapsed by default on mobile */}
          <Collapse in={filterMenuOpen || !isMobile}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6">Filtros</Typography>
                <Button
                  size="small"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => {
                    setSelectedMainCategory('');
                    setSelectedSubCategory('');
                    setSortBy('createdAt-desc');
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    fontSize: '0.875rem'
                  }}
                >
                  Limpiar
                </Button>
              </Box>

              {/* Category Filter */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Categoría</Typography>
                <List dense>
                  {Object.entries(CATEGORY_HIERARCHY).map(([key, category]) => (
                    // Only show main category if it has products
                    categoryHasProducts(key, products) && (
                      <React.Fragment key={key}>
                        <ListItem
                          disablePadding
                          sx={{
                            backgroundColor: selectedMainCategory === key ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                          }}
                        >
                          <ListItemButton
                            onClick={() => {
                              if (selectedMainCategory === key) {
                                setSelectedMainCategory('');
                                setSelectedSubCategory('');
                                setExpandedCategories([]);
                              } else {
                                setSelectedMainCategory(key);
                                setSelectedSubCategory('');
                                setExpandedCategories([key]);
                              }
                                if (isMobile) setFilterMenuOpen(false);
                            }}
                          >
                            <ListItemText
                              primary={category.name}
                              primaryTypographyProps={{
                                fontSize: '0.9rem',
                                fontWeight: selectedMainCategory === key ? 'bold' : 'normal'
                              }}
                            />
                          </ListItemButton>
                        </ListItem>

                        {(selectedMainCategory === key || expandedCategories.includes(key)) && (
                          <List component="div" dense disablePadding>
                            {getSubcategories(selectedMainCategory).map((subCategory, index) => (
                              // Only show subcategory if it has products
                                categoryHasProducts(subCategory, products, selectedMainCategory) && (
                                <React.Fragment key={index}>
                                  <ListItem
                                    disablePadding
                                    sx={{ pl: 4,
                                      backgroundColor: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                    }}
                                  >
                                    <ListItemButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (selectedSubCategory === subCategory) {
                                          setSelectedSubCategory('');
                                          setExpandedCategories([selectedMainCategory]);
                                        } else {
                                          setSelectedSubCategory(subCategory);
                                          setExpandedCategories([selectedMainCategory, subCategory]);
                                        }
                                          if (isMobile) setFilterMenuOpen(false);
                                      }}
                                    >
                                      <ListItemText
                                        primary={subCategory}
                                        primaryTypographyProps={{
                                          fontSize: '0.85rem',
                                          fontWeight: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'bold' : 'normal',
                                        }}
                                      />
                                    </ListItemButton>
                                  </ListItem>

                                  {/* Only show leaf categories if they exist and parent is expanded */}
                                  {(selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) && 
                                   getLeafCategories(selectedMainCategory, subCategory).length > 0 && (
                                    <ListItem>
                                      <List dense sx={{ pl: 2 }}>
                                        {getLeafCategories(selectedMainCategory, subCategory)
                                            .filter(leafCategory => categoryHasProducts(leafCategory, products, selectedMainCategory))
                                          .map((leafCategory, index) => (
                                          <ListItem
                                            key={index}
                                            disablePadding
                                            sx={{ pl: 4,
                                              backgroundColor: selectedSubCategory === leafCategory ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                            }}
                                          >
                                            <ListItemButton
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedSubCategory(leafCategory);
                                                setExpandedCategories([selectedMainCategory, subCategory]);
                                                  if (isMobile) setFilterMenuOpen(false);
                                              }}
                                            >
                                              <ListItemText
                                                primary={leafCategory}
                                                primaryTypographyProps={{
                                                  fontSize: '0.8rem',
                                                  fontWeight: selectedSubCategory === leafCategory ? 'bold' : 'normal'
                                                }}
                                              />
                                            </ListItemButton>
                                          </ListItem>
                                        ))}
                                      </List>
                                    </ListItem>
                                  )}
                                </React.Fragment>
                              )
                            ))}
                          </List>
                        )}
                      </React.Fragment>
                    )
                  ))}
                </List>
              </Box>

              {/* Sort Options */}
              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ordenar por</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Ordenar por"
                  >
                    <MenuItem value="createdAt-desc">Más recientes</MenuItem>
                    <MenuItem value="createdAt-asc">Más antiguos</MenuItem>
                    <MenuItem value="views-desc">Más vistos</MenuItem>
                    <MenuItem value="views-asc">Menos vistos</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {isMobile && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        width: '100%',
                        pt: 3
                      }}
                      onClick={() => setFilterMenuOpen(!filterMenuOpen)}
                    >
                    
                      <IconButton size="small">
                          <ExpandLessIcon sx={{ fontSize: 35 }} />
                         
                      </IconButton>
                    </Box>
                  )}
            </Paper>
          </Collapse>
        </Grid>

          {/* Products Grid - 2 per row */}
          <Grid item xs={12}>
          <Grid 
            container 
              spacing={1}
            sx={{ 
              display: 'flex',
              justifyContent: 'flex-start'
            }}
          >
            {(filteredProducts.length > 0 ? filteredProducts : []).map((product) => (
              <Grid 
                item 
                  xs={6}
                key={product._id}
                >
                  <Box
                    elevation={1}
                    onClick={() => {
                      navigate(`/product/${product._id}`);
                    }}
                    to={`/product/${product._id}`}
                    href={`/product/${product._id}`}
                sx={{
                      height: '100%',
                  display: 'flex',
                      flexDirection: 'column',
                      borderRadius: "4px",
                      overflow: 'hidden',
                      p:1.5,
                      bgcolor: 'rgb(255, 255, 255)',
                      border: '0.5px solid rgba(0, 0, 0, 0.12)'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        paddingTop: '100%', // Square aspect ratio
                        backgroundColor: 'rgb(255, 255, 255)'
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={product.imageUrls?.[0] || LdPlaceholder}
                        alt={product.name}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          backgroundColor: 'rgb(255, 255, 255)'
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block',
                          backgroundColor: 'rgba(255, 136, 0, 0.75)',
                          padding: '2px 4px',
                          borderRadius: '1px',
                          position: 'absolute',
                          bottom:5,
                          px:0.5,
                          color: 'white',
                          right: 5,
                          zIndex: 1000
                         }}
                      >
                        {product.category}
                      </Typography>
                    </Box>
                    <Box sx={{mt:1}}>
                      <Typography
                        sx={{
                          fontWeight: '500',
                          overflow: 'hidden',
                          fontSize: '13px',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.2,
                          minHeight: '2.4em',
                          alignItems: 'center',
                          verticalAlign: 'middle'
                        }}
                      >
                        {product.name}
                      </Typography>
                      <Divider sx={{my: 1}}/>
                    
                      <Box sx={{display: 'flex', flexDirection: 'column', backgroundColor: 'rgba(0, 0, 0, 0.01)', borderRadius: '2px', alignItems: 'flex-start'}}>
                      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="baseline" sx={{ lineHeight: 1 }}>
                      <Typography variant="h4" sx={{fontWeight: '550', fontSize: '20px', color: 'rgb(255, 102, 0)'}}>
                        ${product.priceBreaks?.[product.priceBreaks.length - 1]?.price || (product.basePrice || 'Consultar precio')}
                      </Typography>
                      <Typography variant="h6" sx={{fontWeight: '550', fontSize: '15px', color: 'rgb(255, 118, 27)', flexShrink: 1, lineHeight: 1.1}}>
                      por {UNIT_LABELS_SINGULAR[product.unit]}
                      </Typography>
                      </Box>
                      <Typography variant="caption" color="rgb(94, 94, 94)" sx={{fontWeight: '500', fontSize: '12px'}}>
                        {product.priceBreaks?.length > 0 
                          ? `Desde ${product.priceBreaks[product.priceBreaks.length - 1].minQuantity} ${UNIT_LABELS_PLURAL[product.unit]}`
                          : `Precio unitario`
                        }
                      </Typography>
                      </Box>
                    </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
        </Box>

        {/* Copy Dialog */}
      <Dialog
        open={showCopyDialog}
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            minWidth: 'auto',
            p: 1,
            borderRadius: 2,
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            boxShadow: 'none',
          }
        }}
      >
        <DialogContent sx={{ p: 1 }}>
          <Typography>Link copiado</Typography>
        </DialogContent>
      </Dialog>

        <EditButtonDialog
          open={editDialogOpen}
          onClose={() => {
            console.log('Dialog closing'); // Debug log
            setEditDialogOpen(false);
            setEditingButton(null);
          }}
          button={editingButton}
          onSave={handleSaveButton}
          availableIcons={AVAILABLE_ICONS}
          distributor={distributor}
        />

        <EditStyleDialog
          open={styleDialogOpen}
          onClose={() => setStyleDialogOpen(false)}
          style={customStyle}
          onSave={handleSaveStyle}
        />
    </Container>
    );
  }


  return (
    <Container sx={{ 
      py: 2, 
      bgcolor: customStyle.catalogBackgroundColor 
    }}>
      {/* Distributor Profile Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Profile Image Section */}
          <Grid item xs={12} md={3}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          >
            <Box sx={{ 
              textAlign: 'center',
              position: 'relative',
            }}>
              {(distributor.profileImage || distributor.logoUrl) ? (
                <Avatar
                  src={distributor.profileImage || distributor.logoUrl}
                  alt={distributor.name}
                  sx={{
                    width: 200,
                    height: 200,
                    margin: 'auto',
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.1)'
                  }}
                />
              ) : (
                <Avatar sx={{ 
                  width: 200, 
                  height: 200, 
                  margin: 'auto',
                  bgcolor: 'grey.200' 
                }}>
                  <StoreIcon sx={{ fontSize: 100, color: 'grey.500' }} />
                </Avatar>
              )}
            </Box>
          </Grid>

          {/* Info Section */}
          <Grid item xs={12} md={9}
          sx={{
            border: {xs: 'none', md: '1px solid'},
            borderColor: {xs: 'none', md: 'rgba(0, 0, 0, 0.1)'},
            borderRadius: '4px',
            p: 2,
            mt: 2,
            position: 'relative'
          }}
          >
            <IconButton onClick={handleShare} sx={{position: 'absolute', top: 0, right: 0, p: 1}}>
              <ShareIcon sx={{ color: customStyle.secondaryTextColor }} />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" sx={{ color: customStyle.nameColor }}>
                {distributor.name}
              </Typography>
              {distributor.status === 'verified' && (
                <Tooltip title="Este vendedor ha sido verificado por Liderdist.">
                  <VerifiedIcon 
                    color="primary" 
                    sx={{ 
                      fontSize: 28,
                      verticalAlign: 'middle' 
                    }} 
                  />
                </Tooltip>
              )}
            </Box>

            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              sx={{ mb: 2, color: customStyle.descriptionColor }}
            >
              {distributor.governmentName}
            </Typography>

            <Box sx={{ my: 2 }}>
              <Typography variant="body1" sx={{ color: customStyle.descriptionColor }}>
                {distributor.description}
              </Typography>
            </Box>

            <Box sx={{ mb: 2,
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
              p: 2
             }}>
              <Typography variant="subtitle2" gutterBottom>
                Zonas de Distribución
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {distributor.zones.map((zone, index) => (
                  <Chip
                    key={index}
                    icon={<LocationOnIcon sx={{ fontSize: '0.9rem', color: 'rgb(255, 136, 0)' }} />}
                    label={zone}
                    size="small"
                    sx={{ fontSize: '0.8rem' }}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 2, color: 'text.secondary' }}>
              {distributor.city && <Typography variant="body2" sx={{ color: customStyle.descriptionColor }}>
                {distributor.city}, {distributor.province}
              </Typography>}
              <Typography variant="body2" sx={{ color: customStyle.descriptionColor }}>
                {distributor.phone}
              </Typography>
              <Typography variant="body2" sx={{ color: customStyle.descriptionColor }}>
                {distributor.email}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

 

      {/* Products Section with Filters */}
      <Grid container spacing={3}>
        {/* Filters Section */}
        <Grid item xs={12} md={3}>
          {isMobile && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              >
                <Typography variant="h6">Filtros</Typography>
                <IconButton size="small">
                  {filterMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Paper>
          )}

          <Collapse in={filterMenuOpen || !isMobile}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6">Filtros</Typography>
                <Button
                  size="small"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => {
                    setSelectedMainCategory('');
                    setSelectedSubCategory('');
                    setSortBy('createdAt-desc');
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    fontSize: '0.875rem'
                  }}
                >
                  Limpiar
                </Button>
              </Box>

              {/* Category Filter */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Categoría</Typography>
                <List dense>
                  {Object.entries(CATEGORY_HIERARCHY).map(([key, category]) => (
                    // Only show main category if it has products
                    categoryHasProducts(key, products) && (
                      <React.Fragment key={key}>
                        <ListItem
                          disablePadding
                          sx={{
                            backgroundColor: selectedMainCategory === key ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                          }}
                        >
                          <ListItemButton
                            onClick={() => {
                              if (selectedMainCategory === key) {
                                setSelectedMainCategory('');
                                setSelectedSubCategory('');
                                setExpandedCategories([]);
                              } else {
                                setSelectedMainCategory(key);
                                setSelectedSubCategory('');
                                setExpandedCategories([key]);
                              }
                            }}
                          >
                            <ListItemText
                              primary={category.name}
                              primaryTypographyProps={{
                                fontSize: '0.9rem',
                                fontWeight: selectedMainCategory === key ? 'bold' : 'normal'
                              }}
                            />
                          </ListItemButton>
                        </ListItem>

                        {(selectedMainCategory === key || expandedCategories.includes(key)) && (
                          <List component="div" dense disablePadding>
                            {getSubcategories(selectedMainCategory).map((subCategory, index) => (
                              // Only show subcategory if it has products
                              categoryHasProducts(subCategory, products, selectedMainCategory) && (
                                <React.Fragment key={index}>
                                  <ListItem
                                    disablePadding
                                    sx={{ pl: 4,
                                      backgroundColor: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                    }}
                                  >
                                    <ListItemButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (selectedSubCategory === subCategory) {
                                          setSelectedSubCategory('');
                                          setExpandedCategories([selectedMainCategory]);
                                        } else {
                                          setSelectedSubCategory(subCategory);
                                          setExpandedCategories([selectedMainCategory, subCategory]);
                                        }
                                      }}
                                    >
                                      <ListItemText
                                        primary={subCategory}
                                        primaryTypographyProps={{
                                          fontSize: '0.85rem',
                                          fontWeight: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'bold' : 'normal',
                                        }}
                                      />
                                    </ListItemButton>
                                  </ListItem>

                                  {/* Only show leaf categories if they exist and parent is expanded */}
                                  {(selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) && 
                                   getLeafCategories(selectedMainCategory, subCategory).length > 0 && (
                                    <ListItem>
                                      <List dense sx={{ pl: 2 }}>
                                        {getLeafCategories(selectedMainCategory, subCategory)
                                          .filter(leafCategory => categoryHasProducts(leafCategory, products, selectedMainCategory))
                                          .map((leafCategory, index) => (
                                          <ListItem
                                            key={index}
                                            disablePadding
                                            sx={{ pl: 4,
                                              backgroundColor: selectedSubCategory === leafCategory ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                            }}
                                          >
                                            <ListItemButton
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedSubCategory(leafCategory);
                                                setExpandedCategories([selectedMainCategory, subCategory]);
                                              }}
                                            >
                                              <ListItemText
                                                primary={leafCategory}
                                                primaryTypographyProps={{
                                                  fontSize: '0.8rem',
                                                  fontWeight: selectedSubCategory === leafCategory ? 'bold' : 'normal'
                                                }}
                                              />
                                            </ListItemButton>
                                          </ListItem>
                                        ))}
                                      </List>
                                    </ListItem>
                                  )}
                                </React.Fragment>
                              )
                            ))}
                          </List>
                        )}
                      </React.Fragment>
                    )
                  ))}
                </List>
              </Box>

              {/* Sort Options */}
              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ordenar por</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Ordenar por"
                  >
                    <MenuItem value="createdAt-desc">Más recientes</MenuItem>
                    <MenuItem value="createdAt-asc">Más antiguos</MenuItem>
                    <MenuItem value="views-desc">Más vistos</MenuItem>
                    <MenuItem value="views-asc">Menos vistos</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {isMobile && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        width: '100%',
                        pt: 3
                      }}
                      onClick={() => setFilterMenuOpen(!filterMenuOpen)}
                    >
                    
                      <IconButton size="small">
                          <ExpandLessIcon sx={{ fontSize: 35 }} />
                         
                      </IconButton>
                    </Box>
                  )}
            </Paper>
          </Collapse>
        </Grid>

        {/* Products Grid */}
        <Grid item xs={12} md={9}>
          <Grid 
            container 
            spacing={2}
            sx={{ 
              display: 'flex',
              justifyContent: 'flex-start'
            }}
          >
            {(filteredProducts.length > 0 ? filteredProducts : []).map((product) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={3}
                key={product._id}
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <ProductCard product={product} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Quick notification dialog */}
      <Dialog
        open={showCopyDialog}
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            minWidth: 'auto',
            p: 1,
            borderRadius: 2,
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            boxShadow: 'none',
          }
        }}
      >
        <DialogContent sx={{ p: 1 }}>
          <Typography>Link copiado</Typography>
        </DialogContent>
      </Dialog>

      <EditButtonDialog
        open={editDialogOpen}
        onClose={() => {
          console.log('Dialog closing'); // Debug log
          setEditDialogOpen(false);
          setEditingButton(null);
        }}
        button={editingButton}
        onSave={handleSaveButton}
        availableIcons={AVAILABLE_ICONS}
        distributor={distributor}
      />

      <EditStyleDialog
        open={styleDialogOpen}
        onClose={() => setStyleDialogOpen(false)}
        style={customStyle}
        onSave={handleSaveStyle}
      />
    </Container>
  );
};

export default DistributorPage; 